// PageAgentDrawer — the right-hand drawer a page puts its helper in (Douglas
// plan T14). It is what Tables and Edit Spec build by hand: a persistent
// drawer (the page stays usable while the helper works) holding PageAgentPane,
// fed the page's own registration from PageToolsProvider, so a page needs only
// a PAGE_HELPERS key, its PageAgentButton and this.

import React from 'react'
import { Drawer } from '@mui/material'
import PageAgentPane from './PageAgentPane'
import usePageAgentHost from '../../hooks/usePageAgentHost'
import { useOptionalPageToolsContext } from '../../pageTools/PageToolsProvider'
import { INSIGHTS_DRAWER_WIDTH } from '../../components/chat/ChatDrawer'

export interface PageAgentDrawerProps {
  /** A PAGE_HELPERS key, e.g. `files`. */
  pageKey: string
  customer: string
  job: string
  open: boolean
  onClose: () => void
}

function Body({ pageKey, customer, job, open, onClose }: PageAgentDrawerProps) {
  const host = usePageAgentHost(customer)
  const ctx = useOptionalPageToolsContext()
  return (
    <PageAgentPane
      pageKey={pageKey}
      customer={customer}
      job={job}
      open={open}
      // The page's address: reopened on it, the chat carries on.
      resumeKey={`${window.location.pathname}${window.location.search}`}
      onClose={onClose}
      getPageContext={() => ctx?.pageContext() ?? ''}
      getTools={() => ctx?.livePageTools() ?? []}
      sessionOptions={host.sessionOptions}
      plugins={host.plugins}
      models={host.models}
    />
  )
}

export default function PageAgentDrawer({ open, ...rest }: PageAgentDrawerProps) {
  // Mounted once opened, so closing keeps the chat for a reopen (resumeKey).
  const [mounted, setMounted] = React.useState(false)
  if (open && !mounted) setMounted(true)
  return (
    <Drawer
      anchor="right"
      variant="persistent"
      open={open}
      onClose={rest.onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: INSIGHTS_DRAWER_WIDTH.width,
          minWidth: INSIGHTS_DRAWER_WIDTH.minWidth,
          maxWidth: INSIGHTS_DRAWER_WIDTH.maxWidth,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        },
      }}
    >
      {mounted && <Body open={open} {...rest} />}
    </Drawer>
  )
}
